import React from 'react';
import Rater from 'react-rater';
import { useHistory } from 'react-router-dom';
import { Grid, Text } from 'elements';
import { makeComma } from 'libs/utils';
import { TCaseMenteeExplain } from 'modules/case/types';
import { CWindowWidth } from 'constants/base.const';
import { Button, Select, Pagination } from 'antd';

type TMenteeExplain = {
  id: string;
  explains: Array<TCaseMenteeExplain>;
  hasWrite: boolean;
  page: number;
  limit: number;
  total: number;
  order: string;
  setPage: (args1: number) => void;
  setOrder: (args1: string) => void;
  selectExplain: (args1: number) => void;
};

const MenteeExplain = ({
  id,
  explains,
  hasWrite,
  page,
  limit,
  total,
  order,
  setPage,
  setOrder,
  selectExplain,
}: TMenteeExplain) => {
  const history = useHistory();
  const setRate = (ratings: number | Array<string | number>) => {
    return Array.isArray(ratings) && ratings[0] ? Number(ratings[0]) : 0;
  };

  const setRateText = (ratings: number | Array<string | number>) => {
    return Array.isArray(ratings) && ratings[1] ? `“${ratings[1]}“` : '';
  };

  return (
    <Grid maxWidth={`${CWindowWidth}px`} padding='50px 50px 0 50px' sm={{ padding: '0 20px' }}>
      <Grid padding='35px 0' sm={{ padding: '20px 0' }}>
        <Grid gap='50px' align='flex-end' sm={{ align: 'center' }}>
          <Text size='2rem' sm={{ size: '1.6rem' }}>
            멘티 해설
          </Text>
          {hasWrite && (
            <Grid isInline padding='4px 0'>
              <Button className='outline small' onClick={() => history.push(`/case/board/${id}/write`)}>
                내 해설 올리기
              </Button>
            </Grid>
          )}
        </Grid>
        <Grid marginTop='12px' sm={{ marginTop: '8px' }}>
          <Text size='1.1rem'>멘티들이 풀이한 해설. 여러분의 풀이법을 자유롭게 공유해 주세요.</Text>
        </Grid>
        <Grid marginTop='12px' sm={{ marginTop: '8px' }}>
          <Grid justify='space-between' align='center'>
            <Text>총 {total}개의 해설</Text>
            <Select value={order} onChange={setOrder}>
              <Select.Option value='rating'>평점순</Select.Option>
              <Select.Option value='new'>최신순</Select.Option>
              <Select.Option value='view'>조회순</Select.Option>
              <Select.Option value='comment'>댓글순</Select.Option>
            </Select>
          </Grid>
        </Grid>
        <Grid marginTop='25px'>
          <Grid md={{ display: 'none' }}>
            {explains.length === 0 ? (
              <Grid justify='center' align='center' height='100px'>
                <Text size='1.2rem'>데이터가 없습니다.</Text>
              </Grid>
            ) : (
              explains.map((explain) => (
                <Grid
                  align='center'
                  key={explain.id}
                  isNoWrap
                  gap='2%'
                  padding='14px 0'
                  marginTop='12px'
                  style={{
                    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
                    cursor: 'pointer',
                  }}
                  onClick={() => selectExplain(explain.id)}
                >
                  <Grid isInline width='10px' />
                  <Grid isInline width='110px'>
                    <Text bold size='2rem'>
                      {explain.mentee_name}
                    </Text>
                  </Grid>
                  <Grid isInline width='40%' lg={{ width: '20%' }}>
                    <Grid>
                      <Text>제목</Text>
                    </Grid>
                    <Grid>
                      <Text className='ellipsis-line-1' size='1.2rem'>
                        {explain.title}
                      </Text>
                    </Grid>
                  </Grid>
                  <Grid isInline width='80px'>
                    <Grid>
                      <Text>작성일</Text>
                    </Grid>
                    <Grid>
                      <Text size='1.2rem'>{explain.created_at}</Text>
                    </Grid>
                  </Grid>
                  <Grid isInline width='140px' xl={{ width: '170px' }}>
                    <Grid>
                      <Text>평점</Text>
                    </Grid>
                    <Grid>
                      <Rater total={6} rating={setRate(explain.ratings)} interactive={false} />
                    </Grid>
                  </Grid>
                  <Grid isInline width='15%'>
                    <Text size='1.2rem' lg={{ size: '1.0rem' }}>
                      {setRateText(explain.ratings)}
                    </Text>
                  </Grid>
                  <Grid isInline width='55px'>
                    <Grid justify='center'>
                      <Text>조회수</Text>
                    </Grid>
                    <Grid justify='center'>
                      <Text size='1.2rem'>{makeComma(explain.hit_count)}</Text>
                    </Grid>
                  </Grid>
                  <Grid isInline width='55px'>
                    <Grid justify='center'>
                      <Text>댓글수</Text>
                    </Grid>
                    <Grid justify='center'>
                      <Text size='1.2rem'>{makeComma(explain.review)}</Text>
                    </Grid>
                  </Grid>
                  <Grid isInline width='10px' />
                </Grid>
              ))
            )}
          </Grid>
          <Grid display='none' md={{ display: 'flex' }} gap='8px 4%'>
            {explains.length === 0 ? (
              <Grid justify='center' align='center' height='100px'>
                <Text size='1.2rem'>데이터가 없습니다.</Text>
              </Grid>
            ) : (
              explains.map((explain) => (
                <Grid
                  align='center'
                  key={explain.id}
                  gap='5px'
                  padding='5px 8px'
                  marginTop='8px'
                  width='48%'
                  sm={{ width: '100%' }}
                  style={{
                    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
                    cursor: 'pointer',
                  }}
                  onClick={() => selectExplain(explain.id)}
                >
                  <Grid isInline width='40px'>
                    <Text bold>{explain.mentee_name}</Text>
                  </Grid>
                  <Grid isInline width='calc(100% - (40px + 55px + 10px))'>
                    <Text ellipsis={1}>{explain.title}</Text>
                  </Grid>
                  <Grid isInline width='55px' gap='5px'>
                    <Text size='0.8rem'>조회수</Text>
                    <Text size='0.8rem'>{makeComma(explain.hit_count)}</Text>
                  </Grid>
                  <Grid isInline width='40px'>
                    <Text bold size='0.8rem'>
                      {explain.created_at}
                    </Text>
                  </Grid>
                  <Grid
                    isInline
                    width='calc(100% - (40px + 55px + 10px))'
                    align='center'
                    justify='center'
                    gap='8px'
                  >
                    <Rater total={5} rating={setRate(explain.ratings)} />
                    <Text size='1.2rem'>{setRateText(explain.ratings)}</Text>
                  </Grid>
                  <Grid isInline width='55px' gap='5px'>
                    <Text size='0.8rem'>댓글수</Text>
                    <Text size='0.8rem'>{makeComma(explain.review)}</Text>
                  </Grid>
                </Grid>
              ))
            )}
          </Grid>
          <Grid marginTop='40px' justify='center'>
            <Pagination current={page} pageSize={limit} responsive total={total} onChange={setPage} />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default MenteeExplain;
